/*
 * Copyright (c) Nexign, JSC, 2022-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.19.0
 */

import { createApi } from '@reduxjs/toolkit/dist/query/react';

import { baseQuery } from '../../api/baseQuery';
import { SUBSCRIBERS_IN_ROAMING_API } from '../app/Api';
import { ECookieKey } from '../storage/storageTypes';
import { removeCookie } from '../storage/storageUtils';
import type {
    ITableApiFilterPresetQuery,
    ITableApiFilterQuery,
    ITableApiListQuery,
    ITableApiMetaQuery,
    ITableApiQuery,
    ITableApiResizeQuery,
    TTableApiSuggestFilterQuery,
    ITableCheckQuery,
    ITableMetaResponse,
    TSetSubscribersInRoamingRequest,
    TTableChangeColumnOrderQuery,
} from './api/types';
import { mapTableFilterSuggestResponse } from './mapping/mapFilterSuggestResponse';
import { mapTableLabelsListResponse } from './mapping/mapLabelsListResponse';
import { mapMetaResponse } from './mapping/mapMetaResponse';
import {
    setBasket,
    setBasketAllIn,
    setBasketCount,
    setBasketParams,
    setTableData,
    setTableSearchAfterValues,
    setFilterSubscribersInRoaming,
    setSubscribersInRoamingCount,
    setSubscribersInRoamingWithFilterCount,
} from './slice';
import { ETableSort } from './types';
import type { ITableBasketData, ITableData, ITableLabelsData } from './types';

export const tableApi = createApi({
    reducerPath: 'tableApi',
    baseQuery,
    tagTypes: ['TableMeta', 'TableList', 'TableLabels'],
    endpoints: (builder) => ({
        /* Meta таблицы */
        getTableMeta: builder.query<ITableMetaResponse, ITableApiMetaQuery>({
            query: ({ url, context }) => ({
                url,
                params: { context },
            }),
            providesTags: ['TableMeta'],
            transformResponse: mapMetaResponse,
        }),

        /* Получение списка (lazy-load) */
        getTableList: builder.query<ITableData<object>, ITableApiListQuery>({
            query: ({ url, from, size, isTemplate, use_search_after, search_after_values, context }) => ({
                url,
                params: { from: from ?? 0, isTemplate, size, use_search_after, search_after_values, context },
            }),
            keepUnusedDataFor: 0,
            providesTags: ['TableList'],
            async onQueryStarted({ from }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    const {
                        elements,
                        count,
                        basketAllIn,
                        searchAfterValues,
                        filterSubscribersInRoaming,
                        subscribersInRoamingCount,
                        subscribersInRoamingWithFilterCount,
                    } = data;
                    /* Данные хранятся в tableSlice */
                    dispatch(
                        setTableData({
                            data: elements || [],
                            from: from || 0,
                            count,
                        }),
                    );
                    /* Статус чекбокса (check-all) */
                    dispatch(setBasketAllIn({ basketAllIn }));
                    /* searchAfterValues для списков, получаемых из elastic search */
                    dispatch(setTableSearchAfterValues({ searchAfterValues }));
                    /* Информация о роуминговых абонентах */
                    dispatch(setFilterSubscribersInRoaming({ filterSubscribersInRoaming }));
                    dispatch(setSubscribersInRoamingCount({ subscribersInRoamingCount }));
                    dispatch(setSubscribersInRoamingWithFilterCount({ subscribersInRoamingWithFilterCount }));
                } catch (err) {
                    console.error(err);
                }
            },
        }),

        /* Фильтрация и сортировка списка */
        setTableFilterAndSort: builder.mutation<void, ITableApiFilterQuery>({
            query: ({ url, filter, sort, context }) => ({
                url,
                params: { context },
                method: 'POST',
                body: {
                    f: filter || {},
                    s: sort?.direction !== ETableSort.default ? sort || {} : {},
                },
            }),
        }),
        /* Предустановленная фильтрация по специальному id фильтра */
        setTableFilterAndSortPreset: builder.query<void, ITableApiFilterPresetQuery>({
            query: ({ url, filterId }) => ({
                url: `${url}?filterId=${filterId}`,
                method: 'POST',
            }),
        }),
        /*
         * Получение facets для фильтров (multiselect & inputSearch).
         * Запрашиваются, если не пришли в meta таблицы
         */
        suggestTableFilter: builder.query<string[], TTableApiSuggestFilterQuery>({
            query: ({ url, value, fieldName, context }) => ({
                url: `${url}/${fieldName}`,
                params: { term: value, context },
            }),
            transformResponse: mapTableFilterSuggestResponse,
        }),
        suggestTableMultiSelectFilter: builder.query<string[], TTableApiSuggestFilterQuery>({
            query: ({ url, value, fieldName, context }) => ({
                url: `${url}/${fieldName}`,
                params: { context },
                method: 'POST',
                body: { term: value },
            }),
            transformResponse: mapTableFilterSuggestResponse,
        }),

        /* Изменение checkbox у таблицы. Для check-all items не посылается */
        check: builder.mutation<{ allIn: boolean; count: number; params?: any }, ITableCheckQuery>({
            query: ({ url, items, value }) => ({
                url: `${url}/${value ? 'add' : 'delete'}`,
                method: 'POST',
                body: items ? { items } : {},
            }),
            /* Обработка ответа от сервера */
            async onCacheEntryAdded(_, { dispatch, cacheDataLoaded }) {
                try {
                    const { data } = await cacheDataLoaded;
                    const { allIn, params, count } = data;
                    /* Статус чекбокса (check-all) */
                    dispatch(setBasketAllIn({ basketAllIn: allIn }));
                    /* Дополнительные параметры для корзины (опционально). */
                    dispatch(setBasketParams({ params }));
                    /* Изменение количества элементов в корзине */
                    dispatch(setBasketCount({ count }));
                    /* Требуется очистка корзины на расходах абонента */
                    removeCookie(ECookieKey.EXPENSE_ANALYZER_PAGE);
                } catch (err) {
                    console.error(err);
                }
            },
        }),
        /* Получение списка выбранных элементов (lazy-load) */
        getBasketList: builder.query<ITableBasketData, ITableApiListQuery>({
            query: ({ url, from, size }) => `${url}?from=${from || 0}&size=${size}`,
            /* Не кешировать данные */
            keepUnusedDataFor: 0,
            /* Обработка ответа от сервера */
            async onQueryStarted({ from }, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    const { elements, count, params } = data;
                    /* Данные хранятся в tableSlice */
                    dispatch(
                        setBasket({
                            data: elements || [],
                            from: from || 0,
                            count,
                            params,
                        }),
                    );
                } catch (err) {
                    console.error(err);
                }
            },
        }),

        /* Изменение ширины столбцов (min 140 - max 440) */
        resizeTable: builder.mutation<void, ITableApiResizeQuery>({
            query: ({ url, columnId, columnSize }) => ({
                url,
                method: 'POST',
                body: {
                    fields: [{ name: columnId, size: columnSize }],
                },
            }),
            structuralSharing: false,
        }),

        /* Настройка столбцов - изменение очередности колонок */
        changeColumnOrder: builder.mutation<void, TTableChangeColumnOrderQuery>({
            query: ({ url, context, fields }) => ({
                url,
                params: { context },
                method: 'POST',
                body: { fields },
            }),
        }),

        getLabelsList: builder.query<ITableLabelsData, ITableApiQuery>({
            query: ({ url }) => url,
            transformResponse: mapTableLabelsListResponse,
            providesTags: ['TableLabels'],
        }),
        setSubscribersInRoaming: builder.mutation<void, TSetSubscribersInRoamingRequest>({
            query: ({ value }) => ({
                url: `${SUBSCRIBERS_IN_ROAMING_API}?value=${value}`,
                method: 'POST',
            }),
            invalidatesTags: ['TableList'],
        }),
    }),
});

export const {
    useSetTableFilterAndSortMutation,
    useLazyGetTableListQuery,
    useSetSubscribersInRoamingMutation,
} = tableApi;
